import {
  ActionName,
  CategoryName,
  ConditionSetItemsP,
  IDatasetRecordView,
  TControlName,
  TFormName,
  TFormValues,
  TUpdaterName,
} from '@infotech-soe/metaschema';
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';

import { arrayExt } from '../../framework/language/ArrayExt';
import { TActionArgsValues } from '../../infrastructure/providers/abstract/IApiProvider';
import { Caption } from '../../models/caption/Caption';
import { MultiStepFormSnapshot } from '../../models/form/form';
import { Signature } from '../../services/commands/types';
import { ClientContext } from '../../services/context/IContextService';
import { DictionaryOutputs } from '../../services/dictionary/IDictionaryService';
import { FormControlViewModel } from '../../services/form/view-model/FormControlViewModel';
import { PredefinedForms } from '../../services/form/view-model/FormCreateViewModel';
import {
  FormSource,
  FormSourceType,
} from '../../services/form/view-model/FormSource';
import {
  FormControls,
  FormViewModel,
} from '../../services/form/view-model/FormViewModel';
import UniqueId from '../../services/uid/UniqueId';

import {
  emptyAction,
  failureAction,
  pendingAction,
  successAction,
} from './actionHandler';

export const enum FormParentType {
  DATASET,
  DICTIONARY,
}

export enum FormCloseReasons {
  COMMON,
  CANCEL,
}

export interface FormParent {
  type: FormParentType;
  id: UniqueId;
}

export interface FormEntity {
  form: FormViewModel;
  parent?: FormParent;
  command: string;
  context: ClientContext;
}

export interface CreateDefaultComplexFormPayload {
  type: FormSourceType.FORM | FormSourceType.PRINT;
  category: CategoryName;
  action: ActionName;
  args: TActionArgsValues;
  dataset: UniqueId;
  title?: Caption;
  command: string;
  multiStep?: MultiStepFormSnapshot;
  context: ClientContext;
  hook?: boolean;
}

export interface CreateConditionComplexFormCreatePayload {
  type: FormSourceType.CONDITION;
  category: CategoryName;
  args: TActionArgsValues;
  dataset: UniqueId;
  formName: TFormName;
  conditions: ConditionSetItemsP;
  title?: Caption;
  command: string;
  hook?: boolean;
}

export interface CreatePredefinedBaseFormCreatePayload {
  type: FormSourceType.PREDEFINED;
  subtype: PredefinedForms;
  category: CategoryName;
  values?: TFormValues;
  parent: FormParent;
  command: string;
}

export interface CreatePredefinedReviewFormCreatePayload extends CreatePredefinedBaseFormCreatePayload {
  subtype: PredefinedForms.REVIEW;
  values: Record<string, any>;
  template: IDatasetRecordView['TemplateCode'];
  width: number;
  variables?: Record<string, any>;
  parseDates?: boolean;
}

export interface CreatePredefinedTreeFormCreatePayload extends CreatePredefinedBaseFormCreatePayload {
  subtype: PredefinedForms.TREE;
  treeId: UniqueId;
  formId?: UniqueId;
  caption?: string;
}

export type CreatePredefinedComplexFormCreatePayload =
  | CreatePredefinedTreeFormCreatePayload
  | CreatePredefinedReviewFormCreatePayload;

export type ComplexFormCreatePayload =
  | CreateDefaultComplexFormPayload
  | CreateConditionComplexFormCreatePayload
  | CreatePredefinedComplexFormCreatePayload;

interface FormUpdateSuccessActionPayload {
  formId: UniqueId;
  controls: FormControls;
  args: Record<TControlName, any>;
}

export interface FormState {
  readonly forms: Record<UniqueId, FormEntity>;
  readonly order: UniqueId[];
}

const initialState: FormState = {
  forms: {},
  order: [],
};

const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    create: pendingAction<FormState, ComplexFormCreatePayload>(),
    createSuccess: successAction<
      FormState,
      {
        form: FormViewModel;
        parent?: FormParent;
        command: string;
        context: ClientContext;
      }
    >((state, { payload }) => {
      const formId = payload.form.id;
      state.forms[formId] = payload;
      state.order.push(formId);
    }),
    createFailure: failureAction(),
    update: pendingAction<
      FormState,
      { formId: UniqueId; field: TControlName; value: any }
    >(),
    updateSuccess: successAction<
      FormState,
      | FormUpdateSuccessActionPayload
      | (FormUpdateSuccessActionPayload & {
          skipNextUpdater: boolean;
          apiVars: Map<string, any>;
        })
    >(),
    updateFailure: failureAction(),
    updateWithOutParams: pendingAction<
      FormState,
      {
        formId: UniqueId;
        field: TControlName;
        value: DictionaryOutputs;
        skipNextUpdater: boolean;
      }
    >(),
    updateWithOutParamsSuccess: successAction<
      FormState,
      {
        formId: UniqueId;
        controls: Record<TControlName, FormControlViewModel>;
        args: Record<TControlName, any>;
        apiVars: Map<string, any>;
        skipNextUpdater: boolean;
      }
    >(),
    updateWithOutParamsFailure: failureAction(),
    updateReviewForm: pendingAction<
      FormState,
      { categoryId: UniqueId; datasetId: UniqueId }
    >(),
    updateReviewFormSuccess: successAction<
      FormState,
      {
        formId: UniqueId;
        controls: FormControls;
        args: TActionArgsValues;
        skipNextUpdater: boolean;
        apiVars: Map<string, any>;
      }
    >(),
    updateReviewFormFailure: failureAction(),
    runSchemaUpdater: pendingAction<
      FormState,
      { formId: UniqueId; field: TControlName; value: any }
    >(),
    runSchemaUpdaterSuccess: successAction<
      FormState,
      {
        formId: UniqueId;
        controls: Record<TControlName, FormControlViewModel>;
        args: Record<TControlName, any>;
        skipNextUpdater: boolean;
        apiVars: Map<string, any>;
      }
    >(),
    runSchemaUpdaterFailure: failureAction(),
    runSingleUpdater: pendingAction<
      FormState,
      { formId: UniqueId; field: TControlName; name?: TUpdaterName }
    >(),
    runSingleUpdaterSuccess: successAction<
      FormState,
      {
        formId: UniqueId;
        controls: Record<TControlName, FormControlViewModel>;
        args: Record<TControlName, any>;
        skipNextUpdater: boolean;
        apiVars: Map<string, any>;
      }
    >(),
    runSingleUpdaterFailure: failureAction(),
    saveHistory: pendingAction<FormState, { formState: FormViewModel }>(),
    saveHistorySuccess: successAction(),
    saveHistoryFailure: failureAction(),
    close: pendingAction<
      FormState,
      { formId: UniqueId; reason?: FormCloseReasons }
    >(),
    closeSuccess: successAction<
      FormState,
      {
        formId: UniqueId;
        formSource: FormSource;
        reason: FormCloseReasons;
        parent?: FormParent;
      }
    >((state, { payload: { formId } }) => {
      delete state.forms[formId];
      arrayExt(state.order).remove(formId);
    }),
    closeFailure: failureAction(),
    submitting(
      state,
      { payload: { formId } }: PayloadAction<{ formId: UniqueId }>
    ) {
      state.forms[formId].form.submitting = true;
    },
    submitted(
      state,
      { payload: { formId } }: PayloadAction<{ formId: UniqueId }>
    ) {
      state.forms[formId].form.submitting = false;
    },
    submit: pendingAction<FormState, { formId: UniqueId }>(),
    submitSuccess: successAction(),
    submitFailure: failureAction(),
    clear: pendingAction<FormState, { formId: UniqueId }>(),
    validateField(
      state,
      {
        payload: { field, formId, invalid },
      }: PayloadAction<{
        formId: UniqueId;
        field: TControlName;
        invalid: boolean;
      }>
    ) {
      const control = state.forms[formId].form.controls[field];
      control.invalid = invalid;
    },
    toggleHints(
      state,
      { payload: { formId } }: PayloadAction<{ formId: UniqueId }>
    ) {
      const { form } = state.forms[formId];
      form.areHintsShown = !form.areHintsShown;
    },
    updateControlProp(
      state,
      {
        payload: { formId, field, propName, propValue },
      }: PayloadAction<{
        formId: UniqueId;
        field: TControlName;
        propName: string;
        propValue: any;
      }>
    ) {
      const control = state.forms[formId].form.controls[field];
      (control as any)[propName] = propValue;
    },
    sign: emptyAction<{
      formId: UniqueId;
      names: TControlName[];
      signatures: Signature[];
    }>(),
    signFailure: failureAction(),
    removeHistoryItem(
      state,
      {
        payload: { formId, field, index },
      }: PayloadAction<{ formId: UniqueId; field: TControlName; index: number }>
    ) {
      const control = state.forms[formId].form.controls[field];
      control.history?.splice(index, 1);
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      isAnyOf(
        formSlice.actions.updateSuccess,
        formSlice.actions.updateWithOutParamsSuccess,
        formSlice.actions.runSchemaUpdaterSuccess,
        formSlice.actions.runSingleUpdaterSuccess,
        formSlice.actions.updateReviewFormSuccess
      ),
      (state, { payload }) => {
        if (payload.formId === '') {
          return;
        }

        const formState = state.forms[payload.formId];

        for (const [id, control] of Object.entries(
          payload.controls as FormControls
        )) {
          formState.form.controls[id] = control;
        }

        for (const [id, arg] of Object.entries(payload.args)) {
          formState.form.args[id] = arg;
        }

        if ('skipNextUpdater' in payload) {
          formState.form.skipNextUpdater = payload.skipNextUpdater;
        }
        if ('apiVars' in payload) {
          formState.form.apiVars = payload.apiVars;
        }
      }
    );
  },
});

export const formReducer = formSlice.reducer;

export default formSlice.actions;
